/**
 * PayablesDeskPage
 * ----------------
 * Everything about money going OUT, moved off the Sourcing Desk so that board
 * stays about sourcing. Four views, switched by ?view= so each one has its own
 * link in the Sourcing Desk menu:
 *   audit          — Invoice Audit (Internal)
 *   parties        — paid & outstanding by supplier / contractor
 *   payments       — disbursements: payments & runs
 *   reconciliation — supplier PO reconciliation
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banknote, Building2, Receipt, Scale } from "lucide-react";
import { useAuth } from "@/App";
import InvoiceAuditDesk from "@/components/admin/InvoiceAuditDesk";
import PayablesByPartyPanel from "@/components/admin/PayablesByPartyPanel";
import PaymentRunsPanel from "@/components/admin/PaymentRunsPanel";
import PoReconciliationPanel from "@/components/admin/PoReconciliationPanel";

const VIEWS = [
  { id: "audit", label: "Invoice Audit", icon: Receipt,
    blurb: "Upload a supplier invoice, audit it against the PO, approve it and pay it." },
  { id: "parties", label: "By supplier / contractor", icon: Building2,
    blurb: "Paid, outstanding and overdue for every party we owe." },
  { id: "payments", label: "Payments & runs", icon: Banknote,
    blurb: "Batch what you have paid, then send remittance and the bookkeeper's report." },
  { id: "reconciliation", label: "PO reconciliation", icon: Scale,
    blurb: "Supplier POs against what was actually invoiced and received." },
];

export default function PayablesDeskPage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = params.get("view");
  const [view, setView] = useState(
    VIEWS.some((v) => v.id === requested) ? requested : "audit");

  useEffect(() => {
    if (requested && VIEWS.some((v) => v.id === requested)) setView(requested);
  }, [requested]);

  const pick = (id) => {
    setView(id);
    setParams({ view: id }, { replace: true });
  };

  const active = VIEWS.find((v) => v.id === view) || VIEWS[0];

  return (
    <div className="space-y-4" data-testid="payables-desk-page">
      <div className="flex flex-wrap gap-2">
        {VIEWS.map((v) => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => pick(v.id)}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-bold transition-colors ${
                view === v.id
                  ? "border-sky-700 bg-sky-700 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
              data-testid={`payables-view-${v.id}`}>
              <Icon className="w-4 h-4" /> {v.label}
            </button>
          );
        })}
      </div>
      <p className="text-[11.5px] text-slate-500 -mt-1">{active.blurb}</p>

      {view === "audit" && <InvoiceAuditDesk token={token} hideParties />}
      {view === "parties" && <PayablesByPartyPanel token={token} />}
      {view === "payments" && <PaymentRunsPanel token={token} />}
      {view === "reconciliation" && <PoReconciliationPanel token={token} />}
    </div>
  );
}
